import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";

type CategoryLite = {
  id: string;
  name: string;
  sortOrder?: number;
  isActive?: boolean;
};

type ProductLite = {
  id: string;
  name: string;
  price: number;
  type?: string;
  preparationType?: string;
  categoryId?: string | null;
  categoryName?: string | null;
  isAvailable?: boolean;
  trackStock?: boolean;
  stock?: number;
  availableStock?: number | null;
  comboItems?: Array<{
    quantity: number;
    product: { id: string; name: string };
  }>;
};

type GroupedCategory = CategoryLite & {
  products: ProductLite[];
};

/**
 * Devuelve el menú completo de un restaurante en una sola llamada:
 * todas las categorías + todos los productos (con su categoría asignada)
 * + sección "Sin categoría" + un resumen.
 *
 * Reemplaza el patrón ineficiente de llamar `searchProduct` muchas veces
 * cuando lo que el LLM realmente necesita es ver el catálogo completo
 * (ej. para evitar duplicados al crear un menú, o para hacer una auditoría).
 *
 * Hace 2 llamadas backend en paralelo: GET product-categories + GET products.
 * Más rápido que N llamadas a searchProduct y devuelve una vista coherente.
 */
export const listMenuSkill: Skill = {
  id: "listMenu",
  description:
    "Devuelve el menú COMPLETO de un restaurante (todas las categorías + todos los productos) en una sola llamada. " +
    "Úsala SIEMPRE como primer paso cuando el usuario te pida: crear varios productos a la vez, auditar el menú, encontrar duplicados, " +
    "reorganizar categorías, o cualquier acción donde necesites una vista global del catálogo. " +
    "Mucho más eficiente que llamar searchProduct uno por uno. Si solo necesitas un producto específico, usa searchProduct.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string", description: "ID del restaurante" },
      includeArchived: {
        type: "boolean",
        description: "Incluir productos archivados (default false).",
      },
    },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) {
      return { ok: false, error: "Falta restaurantId." };
    }
    const includeArchived = Boolean(args.includeArchived);

    if (ctx.follow.enabled) {
      await ctx.follow.step("Cargando menú completo…", { durationMs: 400 });
    }

    try {
      const [catsRes, prodsRes] = await Promise.all([
        apiWithAuth.get(
          `/restaurants/${restaurantId}/product-categories?limit=200&sortBy=sortOrder,name&sortOrder=asc`,
          ctx.accessToken,
        ),
        apiWithAuth.get(
          `/restaurants/${restaurantId}/products?limit=500&sortBy=sortOrder,name&sortOrder=asc&includeArchived=${
            includeArchived ? "true" : "false"
          }`,
          ctx.accessToken,
        ),
      ]);

      const categoriesRaw: CategoryLite[] =
        catsRes.data?.data ?? catsRes.data?.items ?? catsRes.data ?? [];
      const productsRaw: ProductLite[] =
        prodsRes.data?.data ?? prodsRes.data?.items ?? prodsRes.data ?? [];

      // Normalizar productos al shape mínimo (evita inflar contexto del LLM).
      const products: ProductLite[] = productsRaw.map((p) => ({
        id: p.id,
        name: p.name,
        price: p.price,
        type: p.type,
        preparationType: p.preparationType,
        categoryId: p.categoryId ?? null,
        categoryName: p.categoryName ?? null,
        isAvailable: p.isAvailable,
        trackStock: p.trackStock,
        stock: p.stock,
        availableStock: p.availableStock,
        ...(p.type === "combo" && Array.isArray(p.comboItems)
          ? {
              comboItems: p.comboItems.map((ci) => ({
                quantity: ci.quantity,
                product: { id: ci.product.id, name: ci.product.name },
              })),
            }
          : {}),
      }));

      // Agrupar por categoría (orden de las categorías + sin categoría al final).
      const byCategory = new Map<string, ProductLite[]>();
      const uncategorized: ProductLite[] = [];
      for (const p of products) {
        if (p.categoryId) {
          if (!byCategory.has(p.categoryId)) byCategory.set(p.categoryId, []);
          byCategory.get(p.categoryId)!.push(p);
        } else {
          uncategorized.push(p);
        }
      }

      const grouped: GroupedCategory[] = categoriesRaw.map((c) => ({
        id: c.id,
        name: c.name,
        sortOrder: c.sortOrder,
        isActive: c.isActive,
        products: byCategory.get(c.id) ?? [],
      }));

      if (ctx.follow.enabled) {
        await ctx.follow.step(
          `✓ ${categoriesRaw.length} categoría(s), ${products.length} producto(s)`,
          { durationMs: 500, spinner: false },
        );
        ctx.follow.clear();
      }

      const totalCombos = products.filter((p) => p.type === "combo").length;
      const totalUnavailable = products.filter(
        (p) => p.isAvailable === false,
      ).length;

      return {
        ok: true,
        result: {
          summary: {
            totalCategories: categoriesRaw.length,
            totalProducts: products.length,
            totalCombos,
            totalUncategorized: uncategorized.length,
            totalUnavailable,
          },
          categories: grouped,
          uncategorized,
        },
        humanSummary: `Menú: ${categoriesRaw.length} categoría(s), ${products.length} producto(s)${
          totalCombos ? ` (${totalCombos} combo${totalCombos > 1 ? "s" : ""})` : ""
        }${
          uncategorized.length
            ? ` · ${uncategorized.length} sin categoría`
            : ""
        }.`,
      };
    } catch (err) {
      return {
        ok: false,
        error: (err as Error)?.message ?? "Error obteniendo el menú.",
      };
    }
  },
};
